import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  bookingColumnLabel,
  columnsToDisplayPreference,
  displayPreferenceToColumns,
  type BookingReportColumnDisplay,
} from "./bookingLabels";

interface BookingReportColumnEditorProps {
  columns: string[];
  onChange: (columns: string[]) => void;
}

export function BookingReportColumnEditor({
  columns,
  onChange,
}: BookingReportColumnEditorProps) {
  const displayItems = useMemo(
    () => columnsToDisplayPreference(columns),
    [columns],
  );
  const [filterText, setFilterText] = useState("");

  const filteredItems = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return displayItems;
    return displayItems.filter((item) => {
      const label = bookingColumnLabel(item.id).toLowerCase();
      return label.includes(query) || item.id.toLowerCase().includes(query);
    });
  }, [displayItems, filterText]);

  const isFiltering = filterText.trim().length > 0;

  const apply = (next: BookingReportColumnDisplay[]) => {
    onChange(displayPreferenceToColumns(next));
  };

  const handleToggle = (id: string, visible: boolean) => {
    apply(displayItems.map((item) => (item.id === id ? { ...item, visible } : item)));
  };

  const move = (id: string, delta: number) => {
    if (isFiltering) return;
    const index = displayItems.findIndex((item) => item.id === id);
    const nextIndex = index + delta;
    if (index < 0 || nextIndex < 0 || nextIndex >= displayItems.length) return;
    const next = displayItems.slice();
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    apply(next);
  };

  const visibleCount = displayItems.filter((item) => item.visible).length;

  return (
    <div className="flex flex-col gap-2">
      <Input
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        placeholder="Filter columns…"
        type="search"
      />
      <p className="text-xs text-muted-foreground">
        {visibleCount} of {displayItems.length} columns included
      </p>
      {filteredItems.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">No columns match.</p>
      ) : (
        <ul className="max-h-64 overflow-y-auto rounded-md border border-border/60">
          {filteredItems.map((item, index) => {
            const label = bookingColumnLabel(item.id);
            const fullIndex = displayItems.findIndex((row) => row.id === item.id);
            return (
              <li
                key={item.id}
                className={cn(
                  "flex items-center gap-2 border-b border-border/40 px-2 py-1.5 last:border-b-0",
                  !item.visible && "opacity-60",
                )}
              >
                <input
                  type="checkbox"
                  checked={item.visible}
                  onChange={(e) => handleToggle(item.id, e.target.checked)}
                  className="size-3.5 accent-primary"
                  aria-label={label}
                />
                <span className="min-w-0 flex-1 truncate text-sm">{label}</span>
                {!isFiltering && (
                  <span className="flex shrink-0">
                    <button
                      type="button"
                      aria-label={`Move ${label} up`}
                      disabled={fullIndex <= 0}
                      onClick={() => move(item.id, -1)}
                      className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Move ${label} down`}
                      disabled={index < 0 || fullIndex >= displayItems.length - 1}
                      onClick={() => move(item.id, 1)}
                      className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="size-3.5" />
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Use the arrows to set export column order.
      </p>
    </div>
  );
}
